import type { DynamicClient, WalletAccount } from '@dynamic-labs-sdk/client';
import { getActiveNetworkData } from '@dynamic-labs-sdk/client';
import { getNetworkProviders } from '@dynamic-labs-sdk/client/core';

type GetPhantomClusterParams =
  | { dynamicClient: DynamicClient; walletAccount: WalletAccount }
  | { dynamicClient: DynamicClient; walletAccount?: never };

/**
 * Normalizes a cluster string to the value expected by Phantom's deep link protocol.
 * Our API returns "mainnet" but Phantom expects "mainnet-beta".
 */
const normalizeCluster = (cluster: string | undefined): string | undefined => {
  if (cluster === 'mainnet') return 'mainnet-beta';
  return cluster;
};

/**
 * Returns the Solana cluster name expected by Phantom's deep link protocol
 * (e.g. "mainnet-beta", "devnet", "testnet").
 *
 * - When a `walletAccount` is provided, reads the cluster from the active network
 *   data for that account.
 * - When no `walletAccount` is provided (e.g. during `connect` before an account
 *   exists), falls back to the first registered SOL network provider's cluster.
 *
 * Returns `undefined` if no cluster can be determined; Phantom will use the
 * wallet's default in that case.
 */
export const getPhantomCluster = async ({
  dynamicClient,
  walletAccount,
}: GetPhantomClusterParams): Promise<string | undefined> => {
  if (walletAccount) {
    const { networkData } = await getActiveNetworkData(
      { walletAccount },
      dynamicClient
    );

    return normalizeCluster(networkData?.cluster);
  }

  const networkProviders = getNetworkProviders(dynamicClient);
  const solProvider = networkProviders.find((p) => p.chain === 'SOL');

  return normalizeCluster(solProvider?.cluster);
};
